// src/pages/ArticlePage.jsx
import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { getArticle, getRelatedArticles } from '../content/articles.mjs';
import { usePageMeta } from '../lib/seo';

const ArticlePage = () => {
  const { slug } = useParams();
  const article = getArticle(slug);
  const related = getRelatedArticles(slug, 3);

  usePageMeta(
    article
      ? {
          title: article.title,
          description: article.description,
          path: `/articles/${article.slug}/`,
          type: 'article',
        }
      : { title: 'Article not found', description: null, path: null }
  );

  if (!article) {
    return (
      <div className="max-w-3xl mx-auto text-center py-16">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Article not found</h1>
        <Link to="/articles/" className="text-blue-600 hover:underline">
          ← Back to all articles
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto">
      <nav className="text-sm text-gray-500 mb-6">
        <Link to="/" className="text-blue-600 hover:underline">Home</Link>
        {' / '}
        <Link to="/articles/" className="text-blue-600 hover:underline">Articles</Link>
      </nav>
      <article className="bg-white rounded-lg shadow-md p-6 md:p-10">
        <header className="mb-8">
          <h1 className="text-3xl font-bold text-blue-800 mb-3">{article.title}</h1>
          <p className="text-sm text-gray-400">
            Published {article.datePublished}
            {article.dateModified !== article.datePublished && <> · Updated {article.dateModified}</>}
            {article.tags && article.tags.length > 0 && <> · {article.tags.join(' · ')}</>}
          </p>
        </header>
        {article.sections.map((section, i) => (
          <section key={i} className="mb-6">
            {section.heading && (
              <h2 className="text-xl font-semibold text-gray-800 mb-3">{section.heading}</h2>
            )}
            {section.paragraphs.map((p, j) => (
              <p key={j} className="text-gray-700 leading-relaxed mb-4">{p}</p>
            ))}
            {section.list && (
              <ul className="list-disc list-inside text-gray-700 space-y-1 mb-4">
                {section.list.map((item, k) => (
                  <li key={k}>{item}</li>
                ))}
              </ul>
            )}
          </section>
        ))}
        {article.references && article.references.length > 0 && (
          <section className="mt-8 pt-6 border-t border-gray-200">
            <h2 className="text-lg font-semibold text-gray-800 mb-3">Sources and further reading</h2>
            <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
              {article.references.map((ref, i) => (
                <li key={i}>
                  <a href={ref.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                    {ref.title}
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}
        <section className="mt-8 pt-6 border-t border-gray-200">
          <div className="bg-blue-50 rounded-lg p-5">
            <h2 className="text-lg font-semibold text-blue-800 mb-1">Look up a medicine on HealthSA</h2>
            <p className="text-gray-700 mb-3">
              Search live South African medicine prices (Single Exit Price) and SAHPRA registration by
              name, active ingredient or NAPPI code.
            </p>
            <Link to="/" className="inline-block text-sm font-medium text-blue-600 hover:underline">
              Search medicine prices &amp; registration →
            </Link>
          </div>
        </section>
        <p className="mt-8 text-xs text-gray-400">
          {article.automated && (
            <>This article was generated automatically from a curated topic brief and published without
            individual editorial review.{' '}</>
          )}
          This article is general reference information, not medical, pharmaceutical or legal advice.
          Always verify against official sources and consult a healthcare professional.
        </p>
      </article>
      {related.length > 0 && (
        <aside className="mt-8">
          <h2 className="text-lg font-semibold text-gray-800 mb-3">Related articles</h2>
          <ul className="space-y-2">
            {related.map((r) => (
              <li key={r.slug}>
                <Link to={`/articles/${r.slug}/`} className="text-blue-600 hover:underline">
                  {r.title}
                </Link>
              </li>
            ))}
          </ul>
        </aside>
      )}
    </div>
  );
};

export default ArticlePage;
